"use client";

import { useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import { motion } from "framer-motion";
import { CatItem, Rarity, TraitCategory } from "@/types/cat";
import { loadCats, loadTraits } from "@/lib/data/cats";
import { SectionTitle } from "@/components/shared/SectionTitle";
import { FilterBar } from "@/components/collection/FilterBar";
import { VirtualCatGrid } from "@/components/collection/VirtualCatGrid";
import { CatDetailModal } from "@/components/collection/CatDetailModal";
import { RarityFilter } from "@/components/collection/RarityFilter";
import { TraitFilter } from "@/components/collection/TraitFilter";
import { Skeleton } from "@/components/ui/skeleton";

const RARITY_WEIGHT: Record<Rarity, number> = {
  common: 0,
  rare: 1,
  legendary: 2,
  mythic: 3,
};

export default function CollectionPage() {
  const [cats, setCats] = useState<CatItem[]>([]);
  const [traits, setTraits] = useState<TraitCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [selectedRarities, setSelectedRarities] = useState<Rarity[]>([]);
  const [selectedTraits, setSelectedTraits] = useState<Record<string, string[]>>({});
  const [sortBy, setSortBy] = useState("id-asc");
  const [selectedCat, setSelectedCat] = useState<CatItem | null>(null);
  const [fuse, setFuse] = useState<Fuse<CatItem> | null>(null);

  useEffect(() => {
    Promise.all([loadCats(), loadTraits()]).then(([catsData, traitsData]) => {
      setCats(catsData);
      setTraits(traitsData);
      setFuse(
        new Fuse(catsData, {
          keys: ["name", "traits.baseMount", "traits.faceAccessory", "traits.headAccessory", "traits.heldItem", "traits.background"],
          threshold: 0.4,
        })
      );
      setLoading(false);
    });
  }, []);

  const filteredCats = useMemo(() => {
    let result = [...cats];

    // Search
    if (searchValue.trim() && fuse) {
      result = fuse.search(searchValue).map((r) => r.item);
    }

    // Rarity filter
    if (selectedRarities.length > 0) {
      result = result.filter((cat) => selectedRarities.includes(cat.rarity));
    }

    // Trait filters
    Object.entries(selectedTraits).forEach(([key, values]) => {
      if (values.length > 0) {
        result = result.filter((cat) =>
          values.includes(cat.traits[key as keyof CatItem["traits"]])
        );
      }
    });

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "id-desc":
          return b.id - a.id;
        case "rarity-desc":
          return RARITY_WEIGHT[b.rarity] - RARITY_WEIGHT[a.rarity] || b.id - a.id;
        case "luck-desc":
          return b.stats.luck - a.stats.luck || b.id - a.id;
        case "id-asc":
        default:
          return a.id - b.id;
      }
    });

    return result;
  }, [cats, searchValue, selectedRarities, selectedTraits, sortBy, fuse]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <SectionTitle
          subtitle="COLLECTION"
          title="NFT Gallery"
          description="Browse all 1,024 pixel cats"
          centered
        />
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <SectionTitle
        subtitle="COLLECTION"
        title="NFT Gallery"
        description="Browse all 1,024 pixel cats and filter by rarity and traits"
        centered
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mt-8"
      >
        <FilterBar
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selectedRarities={selectedRarities}
          onRaritiesChange={setSelectedRarities}
          selectedTraits={selectedTraits}
          onTraitsChange={setSelectedTraits}
          traits={traits}
          resultCount={filteredCats.length}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />

        <div className="mt-6 flex flex-col gap-6 lg:h-[calc(100vh-180px)] lg:flex-row">
          {/* Desktop filters sidebar */}
          <aside className="hidden w-64 shrink-0 overflow-y-auto lg:block">
            <div className="sticky top-24 space-y-6 rounded-xl border border-border bg-card p-5">
              <h3 className="font-pixel text-sm">Filter</h3>
              <RarityFilter
                selected={selectedRarities}
                onChange={setSelectedRarities}
              />
              <TraitFilter
                traits={traits}
                selected={selectedTraits}
                onChange={setSelectedTraits}
              />
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-1">
              {filteredCats.length === 0 ? (
                <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-dashed border-border text-center">
                  <p className="text-lg font-medium">No matching cats found</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Try adjusting your filters
                  </p>
                </div>
              ) : (
                <VirtualCatGrid
                  cats={filteredCats}
                  onCatClick={setSelectedCat}
                />
              )}
            </div>
          </div>
        </motion.div>

      <CatDetailModal cat={selectedCat} onClose={() => setSelectedCat(null)} />
    </div>
  );
}
